/**
 * 工作台首页（蓝图 §4「聚合入口 + Agent 输入框 + 最新功能位」）。
 *
 * 设计取向：做成「创作者仪表盘」而非营销落地页——进来即见我的数据、继续我的项目、
 * 一键开新创作、逛模板灵感、参与活动。信息架构借鉴成熟平台（数据密度/板块编排），
 * 但**视觉 100% 原创**（我方石墨暖底 + 琥珀节点语言），不照搬竞品画面/资产/文案（合规红线）。
 * 全部走既有能力（canvasLibrary / 导演台闸门），数据概览取本地真实统计；社区作品流为
 * mock 骨架，接真后端后替换。
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Bell,
  Boxes,
  Clapperboard,
  Coins,
  Compass,
  Film,
  Image as ImageIcon,
  Library,
  Music,
  Plus,
  Search,
  Send,
  Sparkles,
  Wand2,
} from 'lucide-react';

import { createTutorialCanvas } from '../../features/canvas/tutorialCanvas';
import { getHomeStats, listCanvases, loadCanvas, newCanvasId, type CanvasMeta, type HomeStats } from '../../features/canvas/canvasLibrary';
import { useCanvasStore } from '../../features/canvas/canvasStore';
import { createEmptyCanvas } from '../../model/canvas';
import { SKILL_TEMPLATES, buildSkillCanvas, type SkillTemplate } from '../../model/skills';
import { loadSkills } from '../../model/skillLoader';
import { DotMatrixBackground } from '../../ui/DotMatrixBackground';
import { useAppCapability } from '../capabilityContext';

function formatWhen(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) {
    return '';
  }
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) {
    return '刚刚';
  }
  if (min < 60) {
    return `${min} 分钟前`;
  }
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    return `${hr} 小时前`;
  }
  const day = Math.floor(hr / 24);
  return day < 30 ? `${day} 天前` : new Date(t).toLocaleDateString();
}

/** 能力快捷入口（图标 + 落点动作） */
const QUICK_ACTIONS = [
  { key: 'new', label: '新建画布', Icon: Plus },
  { key: 'tutorial', label: '教程画布', Icon: Compass },
  { key: 'director', label: '3D 导演台', Icon: Boxes },
  { key: 'generation', label: '图片生成', Icon: ImageIcon },
  { key: 'video', label: '视频生成', Icon: Film },
  { key: 'voice', label: '智能配音', Icon: Music },
  { key: 'storyboard', label: '故事板', Icon: Clapperboard },
  { key: 'editing', label: '智能剪辑', Icon: Wand2 },
] as const;

/** 题材模板已迁至 model/skills 的 SKILL_TEMPLATES（行业技能系统 D-57）。 */

/** 灵感/作品流（mock 骨架：CSS 渐变占位，无假图；接真后换社区/自营作品） */
const SHOWCASE = [
  { title: '「X 小时之后」AI 短片黑客松', tag: '赛事', from: 'from-accent/40', to: 'to-state-info/30' },
  { title: '末世科幻 · 70 集爽文剧情', tag: '剧本', from: 'from-state-info/40', to: 'to-surface-base' },
  { title: 'MV 导演 · 一个说唱 MV', tag: '独家', from: 'from-accent/50', to: 'to-state-warn/30' },
  { title: '角色造型室 · 现代女性', tag: '资产', from: 'from-state-ok/30', to: 'to-accent/30' },
  { title: '创意片头 · 有一剑', tag: '独家', from: 'from-state-danger/30', to: 'to-accent/40' },
] as const;

export function HomeWorkbenchPage() {
  const { gate } = useAppCapability();
  const navigate = useNavigate();
  const [canvases, setCanvases] = useState<CanvasMeta[]>(() => listCanvases());
  const [stats, setStats] = useState<HomeStats>(() => getHomeStats());
  const [skills, setSkills] = useState<SkillTemplate[]>(() => SKILL_TEMPLATES);
  const [idea, setIdea] = useState('');

  useEffect(() => {
    void loadSkills().then(setSkills);
  }, []);

  useEffect(() => {
    setCanvases(listCanvases());
    setStats(getHomeStats());
  }, []);

  const recent = canvases.slice(0, 4);

  function refresh(): void {
    setCanvases(listCanvases());
    setStats(getHomeStats());
  }
  function openNewCanvas(): void {
    useCanvasStore.getState().setDoc(createEmptyCanvas(newCanvasId(), new Date().toISOString()));
    navigate('/canvas/editor');
  }
  function openSkillCanvas(skillId: string): void {
    const skill = skills.find((s) => s.id === skillId);
    if (skill === undefined) {
      return;
    }
    useCanvasStore.getState().setDoc(buildSkillCanvas(skill, newCanvasId(), new Date().toISOString()));
    navigate('/canvas/editor');
  }
  function openTutorial(): void {
    useCanvasStore.getState().setDoc(createTutorialCanvas());
    navigate('/canvas/editor');
  }
  function openCanvas(id: string): void {
    const next = loadCanvas(id);
    if (next === null) {
      refresh();
      return;
    }
    useCanvasStore.getState().setDoc(next);
    navigate('/canvas/editor');
  }
  function runQuick(key: string): void {
    switch (key) {
      case 'new':
        openNewCanvas();
        break;
      case 'tutorial':
        openTutorial();
        break;
      case 'director':
        if (gate?.action === 'enter') {
          navigate('/director');
        } else {
          navigate('/canvas');
        }
        break;
      case 'generation':
      case 'video':
      case 'voice':
        navigate('/generation');
        break;
      default:
        navigate(`/${key}`);
    }
  }

  const statCards = [
    { label: '我的项目', value: stats.projects, Icon: Boxes, to: '/canvas', tone: 'stat-blue' },
    { label: '资产卡', value: stats.assets, Icon: Library, to: '/assets', tone: 'stat-violet' },
    { label: '已生成', value: stats.generations, Icon: Sparkles, to: '/generation', tone: 'stat-cyan' },
    { label: '分镜数', value: stats.shots, Icon: Clapperboard, to: '/assets', tone: 'stat-green' },
  ];

  return (
    <div className="relative h-full overflow-y-auto">
      <DotMatrixBackground className="opacity-50" />
      <div className="relative mx-auto flex min-h-full w-full max-w-[1760px] flex-col px-6 py-8">
        {/* 顶部赛事横幅（图片替代原消息播报、强调视觉；点击进团队/活动） */}
        <button type="button" onClick={() => navigate('/team')} className="panel mb-5 block w-full overflow-hidden p-0 text-left">
          <img
            src="/images/contest-home.png"
            alt="创作者挑战赛 · 短剧大赛"
            className="block h-40 w-full object-fill transition-transform duration-300 hover:scale-[1.02]"
          />
        </button>

        {/* 账户经济区（静态占位，接真后填余额/消息） */}
        <div className="mb-6 flex items-center justify-end gap-2 text-uiSm">
          <span className="inline-flex items-center gap-1 rounded-full border border-surface-edge bg-surface-overlay px-2.5 py-1 text-ink-secondary">
            <Coins size={13} className="text-accent" /> 积分 <span className="font-num">—</span>
          </span>
          <span className="relative inline-flex items-center gap-1 rounded-full border border-surface-edge bg-surface-overlay px-2.5 py-1 text-ink-secondary">
            <Bell size={13} /> 消息
          </span>
        </div>

        {/* B Hero + Agent 输入 */}
        <header className="mb-8">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-surface-edge bg-surface-overlay/70 px-2.5 py-1 text-uiSm text-accent">
            <Sparkles size={13} />
            VideoAI Pro · 节点式短剧创作工作台
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">你想创作什么，交给 Agent</h1>
          <p className="mt-1 max-w-2xl text-ui text-ink-muted">
            剧本、资产、分镜、生成、剪辑，连成一张创作网——一句话即可编排整条流程。
          </p>
          <div className="mt-4 flex max-w-5xl flex-col gap-4 lg:flex-row lg:items-stretch">
          <div className="agent-glow relative flex-1 rounded-card border border-surface-edge bg-surface-overlay p-3 focus-within:border-accent">
            <div className="mb-2 flex items-center gap-1 text-uiSm">
              {[
                { k: '创作', I: Wand2 },
                { k: '视频', I: Film },
                { k: '图片', I: ImageIcon },
                { k: '配音', I: Music },
              ].map((t, i) => (
                <span
                  key={t.k}
                  className={`inline-flex items-center gap-1 rounded-control px-2 py-1 ${i === 0 ? 'bg-accent/15 font-semibold text-accent' : 'text-ink-muted'}`}
                >
                  <t.I size={13} />
                  {t.k}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    navigate('/agent');
                  }
                }}
                placeholder='描述你的故事想法，或输入“/”使用技能（如 /图像智能超分）…'
                className="min-w-0 flex-1 bg-transparent px-1 py-2 text-ui text-ink-primary outline-none placeholder:text-ink-faint"
              />
              <button type="button" onClick={() => navigate('/agent')} aria-label="交给智能助手" className="btn-primary inline-flex items-center gap-1 px-3 py-1.5">
                <Send size={14} />
                创作
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-uiSm">
              {['短剧创作', '资讯播报', '商品种草', '多宫格生图'].map((s) => (
                <button key={s} type="button" onClick={() => navigate('/agent')} className="rounded-full border border-surface-edge bg-surface-base px-2.5 py-1 text-ink-secondary transition-colors hover:border-accent hover:text-accent">
                  / {s}
                </button>
              ))}
              <button type="button" onClick={() => navigate('/agent')} className="text-ink-faint hover:text-ink-primary">
                更多技能 →
              </button>
            </div>
          </div>
          <a
            href="https://aisom.cn"
            target="_blank"
            rel="noopener noreferrer"
            className="promo-banner flex flex-col justify-center gap-2 overflow-hidden rounded-card border border-surface-edge bg-surface-overlay/70 px-6 py-5 transition-colors hover:border-accent/60 lg:w-[340px]"
          >
            <span className="promo-title text-3xl font-bold leading-tight">找订单，去 AI 梦工场</span>
            <span className="text-ui leading-relaxed text-ink-secondary">连接广告主与创作者，聚焦 AI 视频生成、短剧 / 漫剧创作，让每一个创意都能找到变现的舞台</span>
          </a>
          </div>
        </header>

        {/* C 数据概览 */}
        <section className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {statCards.map((c) => (
            <button key={c.label} type="button" onClick={() => navigate(c.to)} className={`panel home-tile stat-card ${c.tone} flex items-center gap-3 p-4 text-left`}>
              <span className="stat-icon flex h-9 w-9 items-center justify-center rounded-control bg-accent/15 text-accent">
                <c.Icon size={17} />
              </span>
              <div>
                <div className="font-num text-xl font-semibold text-ink-primary">{c.value}</div>
                <div className="text-uiSm text-ink-muted">{c.label}</div>
              </div>
              <ArrowRight size={15} className="ml-auto shrink-0 text-ink-faint" />
            </button>
          ))}
        </section>

        {/* D 能力快捷入口 */}
        <section className="mb-8">
          <h2 className="mb-3 text-ui font-semibold">快速开始</h2>
          <div className="grid grid-cols-4 gap-3 sm:grid-cols-8">
            {QUICK_ACTIONS.map((a) => (
              <button key={a.key} type="button" onClick={() => runQuick(a.key)} className="panel home-tile group flex flex-col items-center gap-1.5 px-2 py-3 text-center">
                <span className="flex h-9 w-9 items-center justify-center rounded-control bg-surface-base text-ink-secondary transition-colors group-hover:bg-accent/15 group-hover:text-accent">
                  <a.Icon size={17} />
                </span>
                <span className="text-uiSm text-ink-secondary group-hover:text-ink-primary">{a.label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* E 继续创作 */}
        <section className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-ui font-semibold">最近项目</h2>
            <button type="button" className="inline-flex items-center gap-1 text-uiSm text-ink-muted hover:text-ink-primary" onClick={() => navigate('/canvas')}>
              全部画布 <ArrowRight size={14} />
            </button>
          </div>
          {recent.length === 0 ? (
            <div className="panel flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
              <p className="text-ui text-ink-secondary">还没有项目</p>
              <p className="max-w-sm text-uiSm text-ink-muted">点「新建画布」开始，或打开教程画布了解结构。</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {recent.map((c) => (
                <article key={c.id} role="button" tabIndex={0} className="panel group flex cursor-pointer flex-col gap-2 p-3" onClick={() => openCanvas(c.id)} onKeyDown={(e) => e.key === 'Enter' && openCanvas(c.id)}>
                  <div className={`h-24 overflow-hidden rounded-control border border-surface-line ${c.cover === undefined ? 'bg-gradient-to-br from-accent/25 to-surface-base' : ''}`}>
                    {c.cover !== undefined && <img src={c.cover} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <h3 className="truncate text-ui font-semibold">{c.title}</h3>
                  <div className="flex items-center gap-1.5 text-uiSm text-ink-muted">
                    <span className="font-num font-semibold text-accent">{c.nodeCount}</span>
                    <span>节点</span>
                    <span className="text-ink-faint">·</span>
                    <span>{formatWhen(c.updatedAt)}</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* F 模板 & 教程中心 */}
        <section className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-ui font-semibold">题材模板 · 一键起片</h2>
            <button type="button" onClick={() => navigate('/agent')} className="inline-flex items-center gap-1 text-uiSm text-ink-muted hover:text-ink-primary">
              技能库 <ArrowRight size={14} />
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {skills.map((t) => (
              <button key={t.id} type="button" onClick={() => openSkillCanvas(t.id)} className="panel group flex flex-col items-start gap-1.5 p-4 text-left">
                <div className="flex w-full items-center gap-2">
                  <span className="text-ui font-semibold">{t.name}</span>
                  <span className="ml-auto rounded bg-accent/15 px-1.5 py-0.5 text-[10px] text-accent">{t.genre}</span>
                </div>
                <p className="text-uiSm text-ink-muted">{t.blurb}</p>
                <span className="mt-1 inline-flex items-center gap-1 text-uiSm text-accent opacity-0 transition-opacity group-hover:opacity-100">
                  用此技能起片 <ArrowRight size={13} />
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* G 灵感 · 作品流（mock 骨架） */}
        <section className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-ui font-semibold">灵感 · 精选作品</h2>
            <div className="flex items-center gap-2">
              {['全部', '影视', '营销', '创意'].map((tab, i) => (
                <span key={tab} className={i === 0 ? 'text-ui font-semibold text-accent' : 'text-uiSm text-ink-muted'}>
                  {tab}
                </span>
              ))}
              <span className="inline-flex items-center gap-1 rounded-control border border-surface-edge bg-surface-base px-2 py-1 text-uiSm text-ink-faint">
                <Search size={13} /> 搜索
              </span>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {SHOWCASE.map((w) => (
              <article key={w.title} className="panel flex flex-col gap-2 overflow-hidden">
                <div className={`flex h-28 items-end bg-gradient-to-br ${w.from} ${w.to} p-2`}>
                  <span className="rounded bg-black/40 px-1.5 py-0.5 text-[10px] text-white">{w.tag}</span>
                </div>
                <p className="px-2 pb-2 text-uiSm text-ink-secondary">{w.title}</p>
              </article>
            ))}
          </div>
          <p className="mt-2 text-uiSm text-ink-faint">社区作品流为占位骨架，接入后端后展示真实精选/自营作品。</p>
        </section>

        {/* 活动赛事位已上移至首页顶部横幅；“进入各工作模块”与左侧导航重复，已删（保留上方“快速开始”动作入口） */}
      </div>
    </div>
  );
}
